<template>
  <benchmark-bar-group
    v-if="loading || props.sector"
    title="Industry Comparison"
    :has-subtitle="!!props.sector"
    :groups="computedGroups"
    :loading="loading"
  >
    <template #subtitle>
      Comparing your metrics against the <strong>{{ props.sector }}</strong> sector average.
    </template>
  </benchmark-bar-group>
</template>

<script setup>
import { computed } from 'vue';
import { INDUSTRY_COMPARISON_GROUPS } from '@/pages/analyticsBenchmark/benchmarkConfig';
import { buildMetricsMap, calculateDelta, formatValue } from '@/pages/analyticsBenchmark/benchmarkUtils';
import BenchmarkBarGroup from '@/pages/analyticsBenchmark/components/BenchmarkBarGroup.vue';

const props = defineProps({
  metrics: { type: Array, default: () => [] },
  sector: { type: String, default: null },
  loading: { type: Boolean, default: false },
});

const metricsMap = computed(() => buildMetricsMap(props.metrics));

const computedGroups = computed(() =>
  INDUSTRY_COMPARISON_GROUPS.map(group => ({
    name: group.name,
    metrics: group.metrics
      .map(config => {
        const metric = config.metricId ? metricsMap.value[config.metricId] : null;
        const companyValue = metric?.company_value ?? null;
        const sectorValue = metric?.sector_value ?? null;
        const unit = metric?.unit || 'percent';
        const delta = config.metricId
          ? calculateDelta(companyValue, sectorValue, {
              deltaFormat: config.deltaFormat,
              polarity: config.polarity,
              unit,
            })
          : { raw: null, formatted: 'TBC', colorClass: 'delta-neutral' };

        const isPercentage = unit === 'percent';
        const maxValue = isPercentage ? 100 : Math.max(0, companyValue || 0, sectorValue || 0) * 1.2 || 100;

        return {
          id: config.metricId || config.label,
          label: config.label,
          description: metric?.description || null,
          tooltipId: `ic-${(config.metricId || config.label).replace(/\s+/g, '-')}`,
          value: companyValue,
          average: sectorValue,
          maxValue,
          formattedValue: config.metricId ? formatValue(companyValue, unit) : 'TBC',
          formattedAverage: config.metricId ? formatValue(sectorValue, unit) : 'TBC',
          deltaFormatted: delta.formatted,
          deltaColorClass: delta.colorClass,
        };
      })
      .filter(m => m.value !== null && Math.round(Number(m.value) * 10) !== 0),
  })).filter(group => group.metrics.length > 0),
);
</script>
